import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTelegram } from '@/hooks/useTelegram';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { Welcome } from '@/pages/Onboarding/Welcome';
import { BatteryA } from '@/pages/Onboarding/BatteryA';
import { HomePage } from '@/pages/Home/HomePage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

type OnboardingStep = 'welcome' | 'battery-a';

function AppRoutes() {
  const { isLoading, isAuthenticated, user, completeOnboarding } = useAuth();
  const { isReady } = useTelegram();

  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>('welcome');

  // Показываем загрузку пока не готовы
  if (!isReady || isLoading) {
    return <LoadingScreen />;
  }

  // Не авторизован
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  // Нужен онбординг (v2: Battery A replaces diagnosis + character creation)
  if (!user?.onboardingCompleted) {
    // Welcome -> BatteryA (with built-in result screen)

    if (onboardingStep === 'welcome') {
      return (
        <Welcome
          userName={user?.firstName || undefined}
          onStart={() => setOnboardingStep('battery-a')}
        />
      );
    }

    if (onboardingStep === 'battery-a') {
      return (
        <BatteryA
          onComplete={() => {
            // Battery A sets onboardingCompleted=true on backend
            // Update frontend store to reflect it
            completeOnboarding();
          }}
          onBack={() => setOnboardingStep('welcome')}
        />
      );
    }

    // Fallback
    return <LoadingScreen />;
  }

  // Полностью авторизован — основное приложение
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
